<template>
  <b-modal
    :id="id"
    ref="bModal"
    size="xs"
    modal-class="addFileModal"
    centered
    :title="$t('component.studyRequest.addFileModal.title')"
    :ok-title="$t('global.attach')"
    ok-variant="-primary btn-sm"
    :ok-disabled="okDisabled"
    :cancel-title="$t('global.cancel')"
    cancel-variant="outline-secondary btn-sm"
    @show="onShow"
  >
    <b-form-group :label="$t('component.studyRequest.addFileModal.labelFileType')">
      <vt-combo-select
        ref="addAttachmentType"
        v-model="attachmentTypeSelected"
        qa="add-file-modal-file-type-select"
        :options="attachmentTypes"
        track-by="atyId"
        label="atyDescription"
        :value="selectedValue"
      />
    </b-form-group>
    <b-form-group :label="$t('component.studyRequest.addFileModal.labelFile')">
      <b-form-file
        v-if="!reset"
        id="addAttachment"
        v-model="value.files"
        qa="add-file-modal-file-upload"
        :disabled="!attachmentTypeSelected"
        multiple
        class="form-control-sm form-file"
        :accept="acceptTypes"
        :placeholder="$t('global.noFileChosen')"
        :browse-text="$t('component.studyRequest.addFileModal.exploreButton')"
      />
    </b-form-group>
    <div
      slot="modal-footer"
      class="footer-banner"
      style="width:100%"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            qa="add-file-modal-cancel-button"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <vt-loading-button
            qa="add-file-modal-attach-button"
            :disabled="okDisabled"
            class="btn--primary btn-sm"
            :params="{ promise: attachFile }"
          >
            <i
              class="far fa-paperclip mr-2"
              aria-hidden="true"
            />{{ $t('global.attach') }}
          </vt-loading-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>

import Vue from 'vue'
import attachmentTypeService from '@/api/resources/attachment/attachmentTypeService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import apiExceptionType from '@/utils/apiExceptionType'
const patientAttachmentService = () => import('@/api/resources/attachment/patientAttachmentService')
const requestAttachmentService = () => import('@/api/resources/attachment/requestAttachmentService')
const caseAttachmentService = () => import('@/api/resources/attachment/caseAttachmentService')
const sampleAttachmentService = () => import('@/api/resources/attachment/sampleAttachmentService')
const portionAttachmentService = () => import('@/api/resources/attachment/portionAttachmentService')
const subportionAttachmentService = () => import('@/api/resources/attachment/subportionAttachmentService')
const complementaryAttachmentService = () => import('@/api/resources/attachment/complementaryAttachmentService')
const incidentAttachmentService = () => import('@/api/resources/attachment/incidentAttachmentService')

export default {
    name: 'AddFileModal',
    props: {
        requestCreated: {
            type: Boolean
        },
        incidentCreated: {
            type: Boolean
        },
        entitiesIdArray: {
            type: Array
        },
        attachmentEntityType: {
            type: String
        }
    },
    data() {
        return {
            reset: false, // file inputs are tricky to reset, setting the value to null is not enough
            value: this.getInitValue(),
            attachmentTypes: [],
            selectedValue: null,
            id: null,
            attachmentTypeSelected: null,
            acceptTypes: null,
            errorMessage: null,
            attachmentCall: null
        }
    },
    computed: {
        okDisabled() {
            return this.value.files.length === 0 || !this.value.attachmentType
        }
    },
    watch: {
        attachmentTypeSelected(newValue) {
            this.value = this.getInitValue()
            this.resetFileInput()
            this.value.attachmentType = newValue
            if (newValue) {
                this.acceptTypes = `${newValue.atyFileType.toLowerCase()}/*`
            }
        }
    },
    methods: {
        async show() {
            this.attachmentTypes = await attachmentTypeService.getAttachmentTypes(this.attachmentEntityType)
            this.attachmentTypeSelected = null
            this.acceptTypes = null
            this.$refs.bModal.show()
        },
        async showConcreteAttachmentType(attachmentTypeCode) {
            const attachmentType = await attachmentTypeService.getAttachmentType(this.attachmentEntityType, attachmentTypeCode)
            this.attachmentTypes = [attachmentType]
            this.attachmentTypeSelected = null
            this.acceptTypes = null
            this.selectedValue = this.attachmentTypes[0]
            this.$refs.bModal.show()
        },
        hide() {
            this.$emit('hide')
            this.$refs.bModal.hide()
        },
        onShow() {
            this.value = this.getInitValue()
            this.resetFileInput()
        },
        resetFileInput() {
            this.reset = true
            Vue.nextTick(() => { this.reset = false })
        },
        getInitValue() {
            return { attachmentType: '', files: [ ] }
        },
        async addAttachment() {
            switch (this.attachmentEntityType) {
            case ATTACHMENT_ENTITY_TYPE.PATIENT:
                await patientAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.patientAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.REQUEST:
                await requestAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.requestAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.CASE:
                await caseAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.caseAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.SAMPLE:
                await sampleAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.sampleAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.PORTION:
                await portionAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.portionAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.SUBPORTION:
                await subportionAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.subportionAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.COMPLEMENTARY:
                await complementaryAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.complementaryAttachmentError'
                })
                break
            case ATTACHMENT_ENTITY_TYPE.INCIDENT:
                await incidentAttachmentService().then(service => {
                    this.attachmentCall = service.default
                    this.errorMessage = 'component.attachModal.error.incidentAttachmentError'
                })
                break
            }
            if (this.attachmentCall) {
                if (this.attachmentEntityType === ATTACHMENT_ENTITY_TYPE.REQUEST && !this.requestCreated) {
                    this.$emit('addAttachmentToNotCreatedRequest')
                } else if (this.attachmentEntityType === ATTACHMENT_ENTITY_TYPE.INCIDENT && !this.incidentCreated) {
                    this.$emit('addAttachmentToNotCreatedIncident')
                } else {
                    await this.addAttachmentsCall()
                }
            }
        },
        async addAttachmentsCall() {
            let errors = []
            let responses = []
            this.updateLoading(true)
            for (const file of this.value.files) {
                const call = this.attachmentCall.addAttachment(...this.entitiesIdArray, this.value.attachmentType.atyId, file)
                const [err, response] = await this.toCall(call)
                if (err) {
                    if (err.response && (err.response.data.code === apiExceptionType.MAX_UPLOAD_SIZE_EXCEEDED ||
                        err.response.status === apiExceptionType.ENTITY_TOO_LARGE)) {
                        this.showErrorModalGlobal(this.$t('component.attachModal.error.maxFileSizeExceeded'))
                    } else {
                        this.showErrorModalGlobal(this.$t(this.errorMessage))
                    }
                    errors.push(err)
                } else {
                    responses.push(response)
                }
            }
            this.updateLoading(false)
            if (errors.length) {
                return errors
            } else {
                this.showToastSuccessGlobal()
                this.$emit('attachmentsAdded', responses)
            }
        },
        async attachFile() {
            await this.addAttachment()
            this.hide()
        }
    }
}

</script>

<style lang="scss">

.addFileModal .modal-footer{
  justify-content: space-between;
}

.addFileModal .custom-file-label::after{
  display: block;
}

.addFileModal .custom-file-label::after {
  content: '\f574' !important;
  font-family: 'Font Awesome 5 Pro', sans-serif !important;
  color: vt-color(primary);
  background: transparent;
  border-left: 0;
  font-size: font-size(normal);
}

.addFileModal .modal-body{
  height: auto !important;
  padding: 10px !important;
}

.addFileModal .iconupload{
  position: absolute;
  top: 37px;
  right: 25px;
  z-index: 3;
  color: vt-color(primary);
}

.addFileModal .custom-file-input{
  width: 90%;
}

</style>
